import { Chip } from '@mui/material';

import type { components } from '@/shared/api/openapi';
import type { GroupType } from '@/shared/types/groupType';
import { useTheme } from '@/theme';

type GroupInformation = components['schemas']['GetUsersEndpoint_GroupInformationResponse'];

const MAX_VISIBLE_GROUPS = 5;
const MAX_GROUP_NAME_LENGTH = 50;

// Helper component to render groups with limit logic
const GroupChips = ({
  groups,
  index,
  groupType,
}: {
  groups: GroupInformation[] | null | undefined;
  index: number;
  groupType: GroupType;
}) => {
  const theme = useTheme();

  if (!groups || groups.length === 0) {
    return null;
  }

  const truncateGroupName = (name: string): string => {
    if (name.length <= MAX_GROUP_NAME_LENGTH) {
      return name;
    }
    return `${name.slice(0, MAX_GROUP_NAME_LENGTH)}...`;
  };

  const totalGroups = groups.length;
  const visibleGroups = totalGroups <= MAX_VISIBLE_GROUPS ? groups : groups.slice(0, MAX_VISIBLE_GROUPS);
  const remainingCount = totalGroups > MAX_VISIBLE_GROUPS ? totalGroups - MAX_VISIBLE_GROUPS : 0;

  const testIdPrefix = groupType === 'allocation' ? 'cell-inventory-group-allocations' : `cell-${groupType}-group`;

  return (
    <>
      {visibleGroups.map((groupInformation, chipIndex) => {
        const groupName = groupInformation.name || '';
        const truncatedName = truncateGroupName(groupName);
        return (
          <Chip
            aria-label={`${groupType}-group-${groupName}`}
            data-testid={`${testIdPrefix}-${index}-${chipIndex}`}
            key={`${groupType}-group-${index}-${chipIndex}`}
            label={truncatedName}
            title={groupName.length > MAX_GROUP_NAME_LENGTH ? groupName : undefined}
            size='small'
            sx={{ mr: 0.5, mb: 0.5, fontSize: theme.hhglobal.typography.body.base.fontSize }}
          />
        );
      })}
      {remainingCount > 0 && (
        <Chip
          aria-label={`${groupType}-group-more-${remainingCount}`}
          data-testid={`${testIdPrefix}-${index}-more`}
          label={`+${remainingCount}`}
          size='small'
          sx={{
            mr: 0.5,
            mb: 0.5,
            backgroundColor: 'transparent',
            fontSize: theme.hhglobal.typography.body.large.fontSize,
          }}
        />
      )}
    </>
  );
};

export default GroupChips;
